/**
 * Admin Add-on Catalog Management Page
 *
 * CRUD interface for managing the add-on catalog (Phase 1: Add-ons).
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Plus,
  Pencil,
  Trash2,
  Loader2,
  Package,
  Eye,
  EyeOff,
  X,
  ArrowLeft,
  Filter,
} from 'lucide-react';
import { Link } from 'react-router-dom';

import Button from '../../../../components/base/Button';
import adminService, {
  type Addon,
  type CreateAddonParams,
  type AddonFamily,
  type AddonAudience,
  type AddonFulfillment,
} from '../../../../services/adminService';

const FAMILIES: AddonFamily[] = [
  'VISIBILITY_LIFECYCLE',
  'LISTING_ENRICHMENT',
  'AI_STUDIO',
  'TRUST_VERIFICATION',
  'PROFESSIONAL_CONTENT',
  'PERSONAL',
  'OTHER',
];

const AUDIENCES: AddonAudience[] = ['OWNER', 'BROKER', 'COMPANY', 'ALL'];
const FULFILLMENTS: AddonFulfillment[] = ['AUTO', 'MANUAL_ORDER'];

// ============================================================================
// ADD-ON FORM MODAL
// ============================================================================

interface AddonFormState {
  slug: string;
  name: string;
  nameAr: string;
  description: string;
  descriptionAr: string;
  family: AddonFamily;
  fulfillment: AddonFulfillment;
  audiences: AddonAudience[];
  priceSar: string;
  priceCoins: string;
  isQuoteBased: boolean;
  durationDays: string;
  isActive: boolean;
  sortOrder: string;
  metadata: string;
}

function AddonFormModal({
  addon,
  onSave,
  onClose,
  isLoading,
}: {
  addon?: Addon;
  onSave: (data: CreateAddonParams) => void;
  onClose: () => void;
  isLoading: boolean;
}) {
  const { t, i18n } = useTranslation('addons');
  const isRTL = i18n.language === 'ar';
  const isEdit = !!addon;

  const [formData, setFormData] = useState<AddonFormState>({
    slug: addon?.slug ?? '',
    name: addon?.name ?? '',
    nameAr: addon?.nameAr ?? '',
    description: addon?.description ?? '',
    descriptionAr: addon?.descriptionAr ?? '',
    family: addon?.family ?? 'VISIBILITY_LIFECYCLE',
    fulfillment: addon?.fulfillment ?? 'AUTO',
    audiences: addon?.audiences ?? ['ALL'],
    priceSar:
      addon?.priceSar !== undefined && addon?.priceSar !== null ? String(addon.priceSar) : '',
    priceCoins:
      addon?.priceCoins !== undefined && addon?.priceCoins !== null
        ? String(addon.priceCoins)
        : '',
    isQuoteBased: addon?.isQuoteBased ?? false,
    durationDays:
      addon?.durationDays !== undefined && addon?.durationDays !== null
        ? String(addon.durationDays)
        : '',
    isActive: addon?.isActive ?? true,
    sortOrder: addon?.sortOrder !== undefined ? String(addon.sortOrder) : '0',
    metadata: addon?.metadata ? JSON.stringify(addon.metadata, null, 2) : '',
  });

  const [formError, setFormError] = useState<string | null>(null);

  const toggleAudience = (audience: AddonAudience) => {
    setFormData((prev) => {
      const has = prev.audiences.includes(audience);
      return {
        ...prev,
        audiences: has
          ? prev.audiences.filter((a) => a !== audience)
          : [...prev.audiences, audience],
      };
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);

    if (formData.audiences.length === 0) {
      setFormError(t('admin.audiencesRequired'));
      return;
    }

    // Parse + validate metadata JSON
    let metadata: Record<string, unknown> | null = null;
    if (formData.metadata.trim()) {
      try {
        metadata = JSON.parse(formData.metadata) as Record<string, unknown>;
      } catch {
        setFormError(t('admin.metadataInvalid'));
        return;
      }
    }

    const submitData: CreateAddonParams = {
      slug: formData.slug.trim(),
      name: formData.name.trim(),
      nameAr: formData.nameAr.trim() || undefined,
      description: formData.description.trim() || undefined,
      descriptionAr: formData.descriptionAr.trim() || undefined,
      family: formData.family,
      fulfillment: formData.fulfillment,
      audiences: formData.audiences,
      priceSar: formData.priceSar.trim() ? Number(formData.priceSar) : null,
      priceCoins: formData.priceCoins.trim() ? Number(formData.priceCoins) : null,
      isQuoteBased: formData.isQuoteBased,
      durationDays: formData.durationDays.trim() ? Number(formData.durationDays) : null,
      isActive: formData.isActive,
      sortOrder: formData.sortOrder.trim() ? Number(formData.sortOrder) : 0,
      metadata,
    };

    onSave(submitData);
  };

  const inputClass =
    'w-full px-4 py-2 rounded-lg border dark:bg-gray-700 dark:border-gray-600 dark:text-white';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div
        className="bg-white dark:bg-gray-800 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-auto"
        dir={isRTL ? 'rtl' : 'ltr'}
      >
        <div className="sticky top-0 bg-white dark:bg-gray-800 border-b dark:border-gray-700 p-4 flex justify-between items-center">
          <h2 className="text-xl font-bold text-[var(--pv-color-primary)] dark:text-white">
            {isEdit ? t('admin.editAddon') : t('admin.createAddon')}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {/* Slug */}
          <div>
            <label className="block text-sm font-medium mb-2">{t('admin.fieldSlug')}</label>
            <input
              type="text"
              value={formData.slug}
              onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
              className={`${inputClass} font-mono`}
              placeholder="e.g., featured-listing"
              required
              disabled={isEdit}
            />
            {!isEdit && (
              <p className="text-xs text-gray-500 mt-1">{t('admin.fieldSlugHint')}</p>
            )}
          </div>

          {/* Name EN + AR */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">{t('admin.fieldName')}</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className={inputClass}
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">{t('admin.fieldNameAr')}</label>
              <input
                type="text"
                value={formData.nameAr}
                onChange={(e) => setFormData({ ...formData, nameAr: e.target.value })}
                className={inputClass}
                dir="rtl"
              />
            </div>
          </div>

          {/* Description EN + AR */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">
                {t('admin.fieldDescription')}
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className={`${inputClass} resize-none`}
                rows={2}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">
                {t('admin.fieldDescriptionAr')}
              </label>
              <textarea
                value={formData.descriptionAr}
                onChange={(e) => setFormData({ ...formData, descriptionAr: e.target.value })}
                className={`${inputClass} resize-none`}
                rows={2}
                dir="rtl"
              />
            </div>
          </div>

          {/* Family + Fulfillment */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">{t('admin.fieldFamily')}</label>
              <select
                value={formData.family}
                onChange={(e) =>
                  setFormData({ ...formData, family: e.target.value as AddonFamily })
                }
                className={inputClass}
              >
                {FAMILIES.map((fam) => (
                  <option key={fam} value={fam}>
                    {t(`family.${fam}`)}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">
                {t('admin.fieldFulfillment')}
              </label>
              <select
                value={formData.fulfillment}
                onChange={(e) =>
                  setFormData({ ...formData, fulfillment: e.target.value as AddonFulfillment })
                }
                className={inputClass}
              >
                {FULFILLMENTS.map((f) => (
                  <option key={f} value={f}>
                    {t(`fulfillment.${f}`)}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Audiences */}
          <div>
            <label className="block text-sm font-medium mb-2">{t('admin.fieldAudiences')}</label>
            <div className="flex flex-wrap gap-3">
              {AUDIENCES.map((aud) => (
                <label
                  key={aud}
                  className="flex items-center gap-2 cursor-pointer px-3 py-2 rounded-lg border dark:border-gray-600"
                >
                  <input
                    type="checkbox"
                    checked={formData.audiences.includes(aud)}
                    onChange={() => toggleAudience(aud)}
                    className="w-4 h-4 rounded text-[#004D40]"
                  />
                  <span className="text-sm">{t(`audience.${aud}`)}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Price SAR + Coins */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">{t('admin.fieldPriceSar')}</label>
              <input
                type="number"
                value={formData.priceSar}
                onChange={(e) => setFormData({ ...formData, priceSar: e.target.value })}
                className={inputClass}
                min="0"
                step="0.01"
                placeholder="-"
                disabled={formData.isQuoteBased}
              />
              <p className="text-xs text-gray-500 mt-1">{t('admin.fieldPriceSarHint')}</p>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">
                {t('admin.fieldPriceCoins')}
              </label>
              <input
                type="number"
                value={formData.priceCoins}
                onChange={(e) => setFormData({ ...formData, priceCoins: e.target.value })}
                className={inputClass}
                min="0"
                placeholder="-"
              />
            </div>
          </div>

          {/* Duration + Sort order */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">
                {t('admin.fieldDurationDays')}
              </label>
              <input
                type="number"
                value={formData.durationDays}
                onChange={(e) => setFormData({ ...formData, durationDays: e.target.value })}
                className={inputClass}
                min="0"
                placeholder="-"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">
                {t('admin.fieldSortOrder')}
              </label>
              <input
                type="number"
                value={formData.sortOrder}
                onChange={(e) => setFormData({ ...formData, sortOrder: e.target.value })}
                className={inputClass}
              />
            </div>
          </div>

          {/* Metadata */}
          <div>
            <label className="block text-sm font-medium mb-2">{t('admin.fieldMetadata')}</label>
            <textarea
              value={formData.metadata}
              onChange={(e) => setFormData({ ...formData, metadata: e.target.value })}
              className={`${inputClass} resize-none font-mono text-sm`}
              rows={4}
              dir="ltr"
              placeholder="{}"
            />
          </div>

          {/* Checkboxes */}
          <div className="flex flex-wrap gap-6">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.isQuoteBased}
                onChange={(e) =>
                  setFormData({ ...formData, isQuoteBased: e.target.checked })
                }
                className="w-4 h-4 rounded text-[#004D40]"
              />
              <span className="text-sm">{t('admin.fieldQuoteBased')}</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.isActive}
                onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                className="w-4 h-4 rounded text-[#004D40]"
              />
              <span className="text-sm">{t('admin.fieldActive')}</span>
            </label>
          </div>

          {formError && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
              {formError}
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t dark:border-gray-700">
            <Button variant="outline" onClick={onClose} disabled={isLoading}>
              {t('cancel')}
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  {t('admin.saving')}
                </>
              ) : (
                t('admin.save')
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ============================================================================
// MAIN PAGE
// ============================================================================

export default function AdminAddonsPage() {
  const { t, i18n } = useTranslation('addons');
  const isRTL = i18n.language === 'ar';

  const [addons, setAddons] = useState<Addon[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [editingAddon, setEditingAddon] = useState<Addon | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [familyFilter, setFamilyFilter] = useState<string>('');
  const [error, setError] = useState<string | null>(null);

  const fetchAddons = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await adminService.getAddons({ family: familyFilter || undefined });
      setAddons(data);
    } catch (err) {
      console.error('Failed to fetch add-ons:', err);
      setError(t('loadError'));
    } finally {
      setIsLoading(false);
    }
  }, [familyFilter, t]);

  useEffect(() => {
    fetchAddons();
  }, [fetchAddons]);

  const handleCreate = async (data: CreateAddonParams) => {
    try {
      setIsSaving(true);
      setError(null);
      await adminService.createAddon(data);
      setShowCreateModal(false);
      fetchAddons();
    } catch (err: unknown) {
      console.error('Failed to create add-on:', err);
      setError(err instanceof Error ? err.message : t('admin.createError'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleUpdate = async (data: CreateAddonParams) => {
    if (!editingAddon) return;
    try {
      setIsSaving(true);
      setError(null);
      // Slug cannot be changed.
      const { slug: _slug, ...updateData } = data;
      void _slug;
      await adminService.updateAddon(editingAddon.id, updateData);
      setEditingAddon(null);
      fetchAddons();
    } catch (err: unknown) {
      console.error('Failed to update add-on:', err);
      setError(err instanceof Error ? err.message : t('admin.updateError'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (addon: Addon) => {
    if (!confirm(t('admin.deleteConfirm', { name: addon.name }))) return;
    try {
      setError(null);
      await adminService.deleteAddon(addon.id);
      fetchAddons();
    } catch (err) {
      console.error('Failed to delete add-on:', err);
      setError(t('admin.deleteError'));
    }
  };

  // Sort by family, then sortOrder, so rows group logically.
  const sortedAddons = useMemo(() => {
    return [...addons].sort((a, b) => {
      if (a.family !== b.family) {
        return FAMILIES.indexOf(a.family) - FAMILIES.indexOf(b.family);
      }
      return a.sortOrder - b.sortOrder;
    });
  }, [addons]);

  const formatPrice = (addon: Addon) => {
    if (addon.isQuoteBased) return t('quoteBased');
    if (addon.priceSar !== undefined && addon.priceSar !== null) {
      return t('priceSar', { price: addon.priceSar.toLocaleString() });
    }
    return t('free');
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-white dark:bg-gray-900 p-8 flex justify-center items-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#004D40]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900 p-6" dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-wrap gap-4 justify-between items-center mb-6">
          <div className="flex items-center gap-4">
            <Link
              to="/admin"
              className="p-2 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg"
            >
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div>
              <h1 className="text-2xl font-bold text-[var(--pv-color-primary)] dark:text-white">
                {t('admin.catalogTitle')}
              </h1>
              <p className="text-gray-500">{t('admin.catalogSubtitle')}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/admin/subscriptions/addon-orders">
              <Button variant="outline">{t('admin.ordersTitle')}</Button>
            </Link>
            <Button onClick={() => setShowCreateModal(true)}>
              <Plus className="w-4 h-4 mr-2" />
              {t('admin.addAddon')}
            </Button>
          </div>
        </div>

        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-xl text-red-600">
            {error}
          </div>
        )}

        {/* Family filter */}
        <div className="mb-6 flex items-center gap-2">
          <Filter className="w-5 h-5 text-gray-400" />
          <select
            value={familyFilter}
            onChange={(e) => setFamilyFilter(e.target.value)}
            className="px-4 py-3 rounded-xl border dark:bg-gray-800 dark:border-gray-700 dark:text-white"
          >
            <option value="">{t('admin.allFamilies')}</option>
            {FAMILIES.map((fam) => (
              <option key={fam} value={fam}>
                {t(`family.${fam}`)}
              </option>
            ))}
          </select>
        </div>

        {/* Table */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border dark:border-gray-700 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-4 py-3 text-start text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('admin.colName')}
                  </th>
                  <th className="px-4 py-3 text-start text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('admin.colFamily')}
                  </th>
                  <th className="px-4 py-3 text-start text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('admin.colAudiences')}
                  </th>
                  <th className="px-4 py-3 text-start text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('admin.colFulfillment')}
                  </th>
                  <th className="px-4 py-3 text-start text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('admin.colPrice')}
                  </th>
                  <th className="px-4 py-3 text-start text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('admin.colStatus')}
                  </th>
                  <th className="px-4 py-3 text-end text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('admin.colActions')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y dark:divide-gray-700">
                {sortedAddons.map((addon) => (
                  <tr key={addon.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <Package className="w-4 h-4 text-[#004D40]" />
                        <div>
                          <p className="font-medium text-gray-900 dark:text-white">
                            {addon.name}
                          </p>
                          {addon.nameAr && (
                            <p className="text-xs text-gray-500" dir="rtl">
                              {addon.nameAr}
                            </p>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                      {t(`family.${addon.family}`)}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        {addon.audiences.map((aud) => (
                          <span
                            key={aud}
                            className="px-2 py-0.5 rounded-full text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300"
                          >
                            {t(`audience.${aud}`)}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                      {t(`fulfillment.${addon.fulfillment}`)}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                      {formatPrice(addon)}
                    </td>
                    <td className="px-4 py-3">
                      {addon.isActive ? (
                        <span className="text-green-600 flex items-center gap-1 text-sm">
                          <Eye className="w-4 h-4" /> {t('admin.colActive')}
                        </span>
                      ) : (
                        <span className="text-gray-400 flex items-center gap-1 text-sm">
                          <EyeOff className="w-4 h-4" /> {t('admin.colInactive')}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-end whitespace-nowrap">
                      <button
                        onClick={() => setEditingAddon(addon)}
                        className="p-2 hover:bg-gray-100 dark:hover:bg-gray-600 rounded-lg"
                        title={t('admin.editAddon')}
                      >
                        <Pencil className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                      </button>
                      <button
                        onClick={() => handleDelete(addon)}
                        className="p-2 hover:bg-red-100 dark:hover:bg-red-900/30 text-red-600 rounded-lg"
                        title={t('cancel')}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
                {sortedAddons.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-4 py-8 text-center text-gray-500">
                      {t('admin.emptyCatalog')}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <AddonFormModal
          onSave={handleCreate}
          onClose={() => setShowCreateModal(false)}
          isLoading={isSaving}
        />
      )}

      {/* Edit Modal */}
      {editingAddon && (
        <AddonFormModal
          addon={editingAddon}
          onSave={handleUpdate}
          onClose={() => setEditingAddon(null)}
          isLoading={isSaving}
        />
      )}
    </div>
  );
}
